import dagre from 'dagre'
import { measureNode } from './measure'

/**
 * Turning what the agent said into an Excalidraw scene.
 *
 * THE AGENT DOES NOT DO GEOMETRY. Excalidraw elements carry absolute x and
 * y, and models are bad at that: the generators that produced this
 * project's own design documents needed an autofit pass AND a collision
 * checker, and still shipped overlapping text twice before the checker
 * existed. A model freehanding coordinates mid-conversation would give
 * you boxes on top of each other, slowly, because it is emitting hundreds
 * of numbers it cannot check.
 *
 * So the agent sends STRUCTURE — nodes, edges, labels — and dagre, which
 * is already a dependency here and is what the workflow designer lays out
 * with, does the arithmetic. The model does the thinking and the layout
 * engine does the geometry, and neither attempts the other's job.
 *
 * WHAT COMES OUT IS A REAL `.excalidraw` SCENE, not a picture of one. It
 * can be dragged, relabelled and drawn on in the product, and written to
 * the workspace as a file that opens in excalidraw.com. That is the whole
 * reason for carrying the dependency.
 */

/** What the agent is allowed to ask for. A closed set, like the parts. */
export interface DiagramNode {
  id: string
  label: string
  /**
   * A labelled region this box belongs to, e.g. "In the venue".
   *
   * Boxes in the same group are laid out together and drawn inside a
   * titled frame. It is how a diagram says WHERE something happens, which
   * is most of what makes a flow legible, and dagre lays out clusters so
   * it costs nothing.
   */
  group?: string
  /**
   * What sort of box. `step` is the default; `decision` and `terminal`
   * exist because a flow with every box identical is a flow nobody can
   * read at a glance.
   */
  kind?: 'step' | 'decision' | 'terminal' | 'note'
}

export interface DiagramEdge {
  from: string
  to: string
  label?: string
}

export interface DiagramRequest {
  title?: string
  nodes: DiagramNode[]
  edges: DiagramEdge[]
  /** Top to bottom by default; left to right for a long pipeline. */
  direction?: 'TB' | 'LR'
}

export interface DiagramScene {
  title?: string
  elements: unknown[]
}

/** Warm stone on the light theme, which is the product's own ink. */
const INK = '#1c1a15'
const MUTED = '#6f6a5f'
const RULE = '#d8cfbd'
const SURFACE = '#fffdf8'
const ACCENT = '#ff2d87'
const NOTE_BG = '#f1ead9'

const FONT_SIZE = 16
/** Excalidraw's own enum: 1 hand-drawn, 2 normal, 3 code. */
const FONT_NORMAL = 2

/**
 * Deterministic ids and seeds.
 *
 * Excalidraw wants a `seed` per element and would otherwise take a random
 * one, which makes the same diagram a different file every render: a
 * useless diff, and a scene that cannot be compared with the one before
 * it. Counting from the content keeps it stable.
 */
function makeCounter(): () => number {
  let n = 1
  return () => n++
}

function base(
  type: string,
  id: string,
  x: number,
  y: number,
  width: number,
  height: number,
  seed: number,
): Record<string, unknown> {
  return {
    id,
    type,
    x,
    y,
    width,
    height,
    angle: 0,
    strokeColor: INK,
    backgroundColor: 'transparent',
    fillStyle: 'solid',
    strokeWidth: 1,
    strokeStyle: 'solid',
    // Flat, like everything else this product draws: a hand-drawn
    // diagram of a booking flow reads as a sketch of a plan rather than
    // the plan.
    roughness: 0,
    opacity: 100,
    groupIds: [],
    frameId: null,
    roundness: { type: 3 },
    seed,
    version: 1,
    versionNonce: seed,
    isDeleted: false,
    boundElements: null,
    updated: 1,
    link: null,
    locked: false,
  }
}

/** How a kind is painted. The only place this is decided. */
function paint(kind: DiagramNode['kind']): {
  shape: 'rectangle' | 'diamond' | 'ellipse'
  stroke: string
  background: string
  strokeStyle: 'solid' | 'dashed'
} {
  if (kind === 'decision') {
    // A diamond, because that is what everybody reads as a question
    // without being told. A rectangle in a different colour is not a
    // flowchart.
    return { shape: 'diamond', stroke: ACCENT, background: 'transparent', strokeStyle: 'solid' }
  }
  if (kind === 'terminal') {
    return { shape: 'ellipse', stroke: INK, background: SURFACE, strokeStyle: 'solid' }
  }
  if (kind === 'note') {
    // Dashed, so an aside does not read as a step in the flow.
    return { shape: 'rectangle', stroke: RULE, background: NOTE_BG, strokeStyle: 'dashed' }
  }
  return { shape: 'rectangle', stroke: INK, background: SURFACE, strokeStyle: 'solid' }
}

/**
 * The rectangle a label can actually live in, inside a shape.
 *
 * A diamond is widest for an instant at its middle and a circle is
 * narrowest at top and bottom, so the usable area is a fraction of the
 * bounding box. Laying text out to the full width of either puts it over
 * the points.
 */
function innerBox(
  shape: 'rectangle' | 'diamond' | 'ellipse',
  width: number,
  height: number,
): { width: number; height: number } {
  if (shape === 'diamond') {
    return { width: Math.round(width * 0.5), height: Math.round(height * 0.5) }
  }
  if (shape === 'ellipse') {
    // The largest rectangle inside an ellipse is the axes over root two.
    return { width: Math.round(width * 0.68), height: Math.round(height * 0.68) }
  }
  return { width: width - 20, height: height - 16 }
}

/**
 * Lay the graph out and build the scene.
 *
 * Pure: the same request always produces the same elements, so this is
 * testable without a browser and a redraw can be diffed against the one
 * before it.
 */
export function compileScene(request: DiagramRequest): DiagramScene {
  const nextSeed = makeCounter()
  // `compound` so groups become dagre clusters: it lays the members out
  // together and hands back a box to draw the region with.
  const graph = new dagre.graphlib.Graph({ compound: true })
  graph.setGraph({
    rankdir: request.direction ?? 'TB',
    ranksep: 90,
    nodesep: 56,
    // Clusters need their own breathing room or the frame clips its
    // members.
    marginx: 24,
    marginy: 24,
  })
  graph.setDefaultEdgeLabel(() => ({}))

  const sized = new Map<string, ReturnType<typeof measureNode>>()
  for (const node of request.nodes) {
    const measured = measureNode(node.label, node.kind ?? 'step', FONT_SIZE)
    sized.set(node.id, measured)
    graph.setNode(node.id, { width: measured.width, height: measured.height })
  }

  /** Group name → the cluster id dagre knows it by. */
  const clusters = new Map<string, string>()
  for (const node of request.nodes) {
    if (!node.group) continue
    let id = clusters.get(node.group)
    if (!id) {
      id = `group-${String(clusters.size)}`
      clusters.set(node.group, id)
      graph.setNode(id, { label: node.group })
    }
    graph.setParent(node.id, id)
  }

  const known = new Set(request.nodes.map((node) => node.id))
  // An edge naming a node that does not exist would make dagre invent a
  // blank box, which looks like a bug in the diagram rather than in the
  // request.
  const edges = request.edges.filter(
    (edge) => known.has(edge.from) && known.has(edge.to),
  )
  for (const edge of edges) graph.setEdge(edge.from, edge.to)

  dagre.layout(graph)

  const elements: Record<string, unknown>[] = []

  /**
   * GROUPS FIRST, so they sit behind their members. Excalidraw paints in
   * array order, and a region drawn after the boxes it contains covers
   * them.
   */
  for (const [label, id] of clusters) {
    const box = graph.node(id) as
      | { x: number; y: number; width: number; height: number }
      | undefined
    if (!box) continue
    const x = box.x - box.width / 2
    const y = box.y - box.height / 2
    const region = base('rectangle', id, x, y - 22, box.width, box.height + 22, nextSeed())
    Object.assign(region, {
      strokeColor: RULE,
      backgroundColor: 'transparent',
      strokeStyle: 'dashed',
      roundness: { type: 3 },
    })
    elements.push(region)

    // The title, outside the members rather than over them.
    const title = base('text', `${id}-title`, x + 10, y - 18, box.width - 20, 14, nextSeed())
    Object.assign(title, {
      text: label,
      originalText: label,
      fontSize: 12,
      fontFamily: FONT_NORMAL,
      textAlign: 'left',
      verticalAlign: 'top',
      containerId: null,
      strokeColor: MUTED,
      lineHeight: 1.25,
      autoResize: true,
    })
    elements.push(title)
  }

  for (const node of request.nodes) {
    const laid = graph.node(node.id) as { x: number; y: number } | undefined
    const measured = sized.get(node.id)
    if (!laid || !measured) continue
    // dagre gives the CENTRE; Excalidraw wants the top left.
    const x = laid.x - measured.width / 2
    const y = laid.y - measured.height / 2

    const look = paint(node.kind)
    const textId = `t-${node.id}`
    /**
     * THE SHAPE CARRIES THE MEANING. Everything used to be a rectangle
     * with a different stroke colour, which is not a flowchart: a
     * decision is a diamond and a start or an end is a pill, because
     * that is what everybody already reads without being told.
     */
    const box = base(look.shape, node.id, x, y, measured.width, measured.height, nextSeed())
    box.strokeColor = look.stroke
    box.backgroundColor = look.background
    box.strokeStyle = look.strokeStyle
    box.roundness = look.shape === 'rectangle' ? { type: 3 } : null
    // Bound text, which is how Excalidraw centres a label inside a shape
    // and keeps it there when the box is dragged.
    box.boundElements = [{ type: 'text', id: textId }]
    elements.push(box)

    /**
     * PRE-WRAPPED, AND `autoResize` OFF. This is what was cutting the
     * labels off.
     *
     * `autoResize: true` tells Excalidraw to grow the text to fit its
     * content rather than wrap it to its container, so "Link your Square
     * account (sign in on Square)" rendered as one line straight through
     * both sides of its box. Writing the newlines in myself, with resize
     * off, makes the result the same every time and independent of
     * whatever Excalidraw's wrapping does next version.
     *
     * `originalText` keeps the unwrapped sentence, which is what
     * Excalidraw re-wraps from if somebody resizes the box by hand.
     */
    const wrapped = measured.lines.join('\n')
    const inner = innerBox(look.shape, measured.width, measured.height)
    const text = base(
      'text',
      textId,
      x + (measured.width - inner.width) / 2,
      y + (measured.height - inner.height) / 2,
      inner.width,
      measured.lines.length * FONT_SIZE * 1.25,
      nextSeed(),
    )
    Object.assign(text, {
      text: wrapped,
      originalText: node.label,
      fontSize: FONT_SIZE,
      fontFamily: FONT_NORMAL,
      textAlign: 'center',
      verticalAlign: 'middle',
      containerId: node.id,
      strokeColor: node.kind === 'note' ? MUTED : INK,
      lineHeight: 1.25,
      autoResize: false,
    })
    elements.push(text)
  }

  for (const edge of edges) {
    /**
     * DAGRE'S OWN ROUTE, not a line between two centres.
     *
     * This was the worst of it: a straight line from box to box cut
     * diagonally through everything between them, so any diagram with
     * more than one rank had arrows crossing boxes. Dagre already works
     * out a path that goes around them; it was simply being thrown away.
     */
    const route = graph.edge({ v: edge.from, w: edge.to }) as
      | { points?: { x: number; y: number }[] }
      | undefined
    const points = route?.points ?? []
    if (points.length < 2) continue

    const originX = points[0].x
    const originY = points[0].y
    const relative = points.map((point) => [point.x - originX, point.y - originY])
    const xs = relative.map((p) => p[0])
    const ys = relative.map((p) => p[1])

    const id = `e-${edge.from}-${edge.to}`
    const arrow = base(
      'arrow',
      id,
      originX,
      originY,
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
      nextSeed(),
    )
    Object.assign(arrow, {
      strokeColor: MUTED,
      points: relative,
      lastCommittedPoint: null,
      // Bound both ends, so dragging a box drags its arrows with it.
      startBinding: { elementId: edge.from, focus: 0, gap: 4 },
      endBinding: { elementId: edge.to, focus: 0, gap: 4 },
      startArrowhead: null,
      endArrowhead: 'arrow',
      roundness: { type: 2 },
      elbowed: false,
    })
    elements.push(arrow)

    if (edge.label) {
      /**
       * On the ROUTE's middle, not the straight line's. A "yes" placed
       * halfway between two centres lands on whatever the arrow went
       * around.
       */
      const middle = points[Math.floor(points.length / 2)]
      const labelId = `l-${id}`
      arrow.boundElements = [{ type: 'text', id: labelId }]
      const label = base('text', labelId, middle.x, middle.y, 80, FONT_SIZE, nextSeed())
      Object.assign(label, {
        text: edge.label,
        originalText: edge.label,
        fontSize: 13,
        fontFamily: FONT_NORMAL,
        textAlign: 'center',
        verticalAlign: 'middle',
        containerId: id,
        strokeColor: MUTED,
        lineHeight: 1.25,
        autoResize: false,
      })
      elements.push(label)
    }
  }

  return { title: request.title, elements }
}

/**
 * The scene as a `.excalidraw` file.
 *
 * The same JSON excalidraw.com opens, so a diagram the agent drew can be
 * saved into the workspace and edited by hand later.
 */
export function toExcalidrawFile(scene: DiagramScene): string {
  return `${JSON.stringify(
    {
      type: 'excalidraw',
      version: 2,
      source: 'https://stampede.ai',
      elements: scene.elements,
      appState: { gridSize: null, viewBackgroundColor: '#ffffff' },
      files: {},
    },
    null,
    1,
  )}\n`
}
